import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { GameCard } from "../types/Card";
import { useParams } from "react-router-dom";
import { useGameState } from "../hooks/Game/useGameState";
import { useSessionContext } from "./SessionContext";
import { useNextQuestion } from "../hooks/Game/useNextQuestion";
import { User } from "../types/Session";
import { TableAnswer } from "../types/Table";
import { Phase, SettingsData } from "../types/GameState";
import { ChatMessage } from "../types/ChatMessage";

const MINUTES = 60000;

type GameContextProps = {
  code: string;
  questionCard?: GameCard;
  handCards: Array<GameCard>;
  showHand: boolean;
  selectedCards: GameCard[];
  setQuestionCard: React.Dispatch<React.SetStateAction<GameCard | undefined>>;
  setHandCards: React.Dispatch<React.SetStateAction<Array<GameCard>>>;
  setShowHand: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedCards: React.Dispatch<React.SetStateAction<GameCard[]>>;
  setTableAnswers: React.Dispatch<React.SetStateAction<TableAnswer[]>>;
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  getGameState: (code: string) => void;
  players: Partial<User>[];
  onlinePlayers: Partial<User>[];
  tableAnswers: Partial<TableAnswer>[];
  phase: Phase;
  round: number;
  gameStarted: boolean;
  votingStarted: boolean;
  votingFinished: boolean;
  adminId?: string;
  phaseStartedAt?: string;
  settings?: SettingsData;
  answerTimeLimit: number;
  voteTimeLimit: number;
  showTime: boolean;
  finished?: boolean;
  chatMessages: ChatMessage[];
};

const GameContext = createContext<GameContextProps>({
  code: "",
  handCards: [],
  setHandCards: () => null,
  setSelectedCards: () => null,
  setQuestionCard: () => null,
  setShowHand: () => null,
  getGameState: () => null,
  setTableAnswers: () => null,
  setChatMessages: () => null,
  chatMessages: [],
  showHand: false,
  players: [],
  onlinePlayers: [],
  tableAnswers: [],
  selectedCards: [],
  phase: "lobby",
  round: 0,
  gameStarted: false,
  votingStarted: false,
  votingFinished: false,
  showTime: false,
  finished: false,
  answerTimeLimit: 0,
  voteTimeLimit: 0,
});

export const GameContextProvider: React.FC<{
  children: Array<React.ReactNode> | React.ReactNode;
}> = ({ children }) => {
  const code = (useParams().code || "").toUpperCase();

  const [questionCard, setQuestionCard] = useState<GameCard>();
  const [handCards, setHandCards] = useState<Array<GameCard>>([]);
  const [tableAnswers, setTableAnswers] = useState<Array<TableAnswer>>([]);
  const [showHand, setShowHand] = useState(false);
  const [selectedCards, setSelectedCards] = useState<GameCard[]>([]);
  const [players, setPlayers] = useState<Partial<User>[]>([]);
  const [answerTimeLimit, setAnswerTimeLimit] = useState(0);
  const [voteTimeLimit, setVoteTimeLimit] = useState(0);
  const [showTime, setShowTime] = useState(false);

  const { nextQuestion } = useNextQuestion(code);
  const { game, getGameState, chatMessages, setChatMessages } = useGameState(
    code || "",
  );
  const { user, getSession } = useSessionContext();

  const { voteTime, answerTime } = game?.game.settings || {};

  const phase: Phase = game?.game?.phase || "lobby";
  const round = game?.game?.round || 0;
  const phaseStartedAt = game?.game?.phaseStartedAt;

  const gameStarted = phase !== "lobby";
  const votingStarted = phase === "voting" || phase === "results";
  const votingFinished = phase === "results" || phase === "finished";

  const gameExists = !!game;

  useEffect(() => {
    getSession?.();
  }, [getSession, gameExists]);

  const onlinePlayers = useMemo(
    () => players.filter(({ online, id }) => online || id === user?.id),
    [players, user?.id],
  );

  useEffect(() => {
    if (game) {
      const { hand, table } = game;
      setPlayers((p) =>
        JSON.stringify(p) !== JSON.stringify(game.players) ? game.players : p,
      );
      setHandCards((h) =>
        JSON.stringify(h) !== JSON.stringify(hand) ? (hand as any) : h,
      );

      table.answers.sort(({ user_id }) => (user?.id === user_id ? -1 : 0));

      setTableAnswers((t) =>
        JSON.stringify(t) !== JSON.stringify(table.answers)
          ? table.answers.map((answer) => ({
              ...answer,
              user: game.players.find(
                ({ id }) => id === answer.user_id,
              ) as User,
            }))
          : t,
      );

      setQuestionCard((q) =>
        JSON.stringify(q) !== JSON.stringify(table.question)
          ? (table.question as any)
          : q,
      );
    }
  }, [game, getSession, nextQuestion, user?.id, votingStarted]);

  useEffect(() => {
    if (votingStarted || (answerTime && answerTimeLimit <= 0)) {
      setShowHand(false);
    }
  }, [answerTimeLimit, answerTime, votingStarted]);

  useEffect(() => {
    const limitFor = (minutes?: number) => {
      if (!minutes || !phaseStartedAt) {
        return null;
      }
      return new Date(phaseStartedAt).getTime() + minutes * MINUTES - Date.now();
    };

    const tick = () => {
      const answerLimit = phase === "answering" ? limitFor(answerTime) : null;
      const voteLimit = phase === "voting" ? limitFor(voteTime) : null;

      setAnswerTimeLimit(answerLimit ?? 0);
      setVoteTimeLimit(voteLimit ?? 0);
      setShowTime(answerLimit !== null || voteLimit !== null);
    };

    tick();
    const interval = setInterval(tick, 1000);

    return () => clearInterval(interval);
  }, [phase, phaseStartedAt, answerTime, voteTime]);

  return (
    <GameContext.Provider
      value={{
        questionCard,
        handCards,
        showHand,
        selectedCards,
        setQuestionCard,
        setHandCards,
        setShowHand,
        setSelectedCards,
        code,
        getGameState,
        players,
        tableAnswers,
        setTableAnswers,
        phase,
        round,
        gameStarted,
        votingStarted,
        votingFinished,
        adminId: game?.game?.user_id,
        answerTimeLimit,
        voteTimeLimit,
        settings: game?.game?.settings,
        phaseStartedAt,
        showTime,
        finished: game?.game?.finished,
        chatMessages,
        setChatMessages,
        onlinePlayers,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGameContext = () => {
  const context = useContext(GameContext);

  if (!context) {
    throw new Error("useGameContext needs to be under a GameContextProvider");
  }

  return context;
};
